import axios from "axios";

const baseURL = process.env.REACT_APP_API_URL;

const getToken = () => {
  return localStorage.getItem("token") ?? "";
};

const getRefreshToken = () => {
  return localStorage.getItem("refreshToken") ?? "";
};

const client = axios.create({
  baseURL,
});

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const clearAuthData = () => {
  localStorage.removeItem("token");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("organizationId");
  localStorage.removeItem("organizationName");
  localStorage.removeItem("privileges");
  localStorage.removeItem("username");
  window.location.reload();
};

client.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      if (!config.headers) {
        config.headers = {};
      }
      if (!config.headers["Authorization"]) {
        config.headers["Authorization"] = token.startsWith("Bearer ") ? token : `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

client.interceptors.response.use(
  (response) => {
    return response;
  },
  async function (error) {
    const originalRequest = error.config;

    if ((error.response?.status === 401 || error.response?.status === 403) && !originalRequest._retry) {
      const refreshToken = getRefreshToken();

      if (!refreshToken) {
        clearAuthData();
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest._retry = true;
            if (originalRequest.headers) {
              originalRequest.headers["Authorization"] = token as string;
            }
            return client(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const selectedOrganizationId = localStorage.getItem("organizationId");
        const parsedOrganizationId = selectedOrganizationId
          ? Number(selectedOrganizationId)
          : null;
        const refreshPayload: {
          refreshToken: string;
          organizationId?: number;
        } = { refreshToken };
        if (parsedOrganizationId !== null && Number.isSafeInteger(parsedOrganizationId)) {
          refreshPayload.organizationId = parsedOrganizationId;
        }
        const response = await axios.post(`${baseURL}/api/auth/refresh`, {
          ...refreshPayload,
        });

        if (response.data.status === "OK") {
          const newToken = response.data.data.token;
          const newRefreshToken = response.data.data.refreshToken;

          localStorage.setItem("token", newToken);
          localStorage.setItem("refreshToken", newRefreshToken);

          const bearerToken = newToken.startsWith("Bearer ") ? newToken : `Bearer ${newToken}`;
          processQueue(null, bearerToken);

          if (originalRequest.headers) {
            originalRequest.headers["Authorization"] = bearerToken;
          }
          return client(originalRequest);
        } else {
          processQueue(error, null);
          clearAuthData();
          return Promise.reject(error);
        }
      } catch (refreshError: any) {
        processQueue(refreshError, null);
        clearAuthData();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export { client, getToken };
